const KEY_PREFIX = 'dss360_selected_company_'
const LOGOUT_REASON_KEY = 'dss360_logout_reason'
const COMPANY_AUTH_KEY = 'dss360_company_auth'

export const LOGOUT_REASONS = Object.freeze({
  COMPANY_REVOKED: 'company-revoked',
  SESSION_EXPIRED: 'session-expired',
  SESSION_EXPIRED_ACTION_NOT_SAVED: 'session-expired-action-not-saved',
})

export function getCachedCompanyId(userId) {
  if (!userId)
    return null

  try {
    return localStorage.getItem(`${KEY_PREFIX}${userId}`) || null
  }
  catch {
    return null
  }
}

export function setCachedCompany(userId, companyId) {
  if (!userId || !companyId)
    return

  try {
    localStorage.setItem(`${KEY_PREFIX}${userId}`, companyId)
  }
  catch {
    // localStorage unavailable (private mode/blocked): degrade gracefully
  }
}

export function clearCachedCompany(userId) {
  if (!userId)
    return

  try {
    localStorage.removeItem(`${KEY_PREFIX}${userId}`)
  }
  catch {
    // localStorage unavailable: nothing to clear
  }
}

export function setPersistedCompanyAuth({ policy, company }) {
  try {
    localStorage.setItem(COMPANY_AUTH_KEY, JSON.stringify({
      policy: policy || '',
      company: { id: company?._id || company?.id || '' },
    }))
  }
  catch {
    // localStorage unavailable: degrade gracefully
  }
}

export function getPersistedCompanyAuth() {
  try {
    const raw = localStorage.getItem(COMPANY_AUTH_KEY)
    return raw ? JSON.parse(raw) : null
  }
  catch {
    return null
  }
}

export function clearPersistedCompanyAuth() {
  try {
    localStorage.removeItem(COMPANY_AUTH_KEY)
  }
  catch {
    // localStorage unavailable
  }
}

const POST_LOGIN_REDIRECT_KEY = 'dss360_post_login_redirect'
const POST_LOGIN_REDIRECT_TTL_MS = 10 * 60 * 1000

// Where to drop the user after the next login. Persisted because the MSAL
// logout performs a full-page round-trip that kills any in-memory route
// state (and any query param pushed before it).
export function setPostLoginRedirect(path) {
  if (typeof path !== 'string' || !path.startsWith('/'))
    return

  try {
    localStorage.setItem(POST_LOGIN_REDIRECT_KEY, JSON.stringify({ path, savedAt: Date.now() }))
  }
  catch {
    // localStorage unavailable: the user simply lands on the default route
  }
}

export function takePostLoginRedirect() {
  try {
    const raw = localStorage.getItem(POST_LOGIN_REDIRECT_KEY)
    if (!raw)
      return null

    localStorage.removeItem(POST_LOGIN_REDIRECT_KEY)

    const { path, savedAt } = JSON.parse(raw)
    if (typeof path !== 'string' || !path.startsWith('/'))
      return null
    if (!savedAt || Date.now() - savedAt > POST_LOGIN_REDIRECT_TTL_MS)
      return null

    return path
  }
  catch {
    return null
  }
}

export function setLogoutReason(reason) {
  try {
    localStorage.setItem(LOGOUT_REASON_KEY, reason)
  }
  catch {
    // localStorage unavailable: message simply won't be shown
  }
}

export function takeLogoutReason() {
  try {
    const reason = localStorage.getItem(LOGOUT_REASON_KEY)
    if (reason)
      localStorage.removeItem(LOGOUT_REASON_KEY)
    return reason || null
  }
  catch {
    return null
  }
}

const ABSOLUTE_URL_PATTERN = /^(?:https?:)?\/\//i

// Hosts an absolute post-login `redirect` may leave the SPA for: the SPA
// itself and the API host (which also serves the SurveyJS mini-app). Any
// other host is an open redirect and falls back to Home.
function trustedRedirectHosts() {
  const hosts = new Set([globalThis.location.host])

  try {
    hosts.add(new URL(import.meta.env.VITE_API_URL).host)
  }
  catch {
    // VITE_API_URL missing or malformed: only the SPA host is trusted
  }

  return hosts
}

export function resolvePostLoginRedirect(redirect) {
  if (typeof redirect !== 'string' || !redirect)
    return { to: { name: 'Home' } }

  if (!ABSOLUTE_URL_PATTERN.test(redirect))
    return { to: redirect }

  try {
    const url = new URL(redirect, globalThis.location.origin)
    if (trustedRedirectHosts().has(url.host))
      return { href: url.href }
  }
  catch {
    // malformed URL: fall through to Home
  }

  return { to: { name: 'Home' } }
}
